import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import InputMask from 'react-input-mask'

import { Form, Formik } from 'formik'
import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import TextField from '../TextField'

import { ibanRegex, swiftRegex } from '../../helpers'
import theme from '../../theme'

import { CaseTypeType, OtherDataType } from '../../models'
import { useStyles } from '../../pages/styled'
import { isEqual } from 'lodash/fp'
import * as yup from 'yup'
import { useClaimant } from '../../context/col'
import { yupCheckIban, yupCheckPlAccountNumber } from '../../helpers/yupCheckIban'
import ErrorSubmitBack from '../ErrorSubmitBack'
import { Ga4Form } from './Ga4Form'
import { BooleanField } from '../BooleanField'
import { RadioButtonsField } from '../RadioButtonsField'
import { objectToLookup } from '../../helpers/miscFunctions'


// OtherDataForm
const OtherDataForm = (params: {
  otherData: OtherDataType | null
  onPageContinue: (otherData: OtherDataType) => void
  onPageBack: () => void
  isContinueInProgress: boolean
  isDataLoading: boolean
  errorMessage: string
  setIfEqual: (isChanged: boolean) => void
  caseType: CaseTypeType | null
}) => {
  const classes = useStyles(theme)
  const { t } = useTranslation()
  const { state } = useClaimant()

  // Funkce 'handleFormSubmit' je volana z formiku a prijma data ve strukture formiku
  const handleFormSubmit = (values: any) => {
    // 1. vezmu values a ziskam z nich podstruktury CaseReportParams
    // ==> OtherDataType
    const otherDataParams: OtherDataType =
      transformFormikValueToOtherData(values)

    // 2. pošlu data z komponenty nahoru
    params.onPageContinue(otherDataParams)
  }

  // Funkce 'handleBackButtonClicked' je volana z BackButton
  const handleBackButtonClicked = () => {
    params.onPageBack()
  }

  // Transformuje data z aktualniho formikoveho formulare do struktury modelu
  const transformFormikValueToOtherData = (values: FormikData): OtherDataType => {
    return {
      // NAPLNIT DATY Z FORMULARE
      otherInsurance: values.insurance ?? undefined,
      otherInsurer: values.insuranceCompany ?? undefined,
      bankCard: values.card ?? undefined,
      cardIssuer: values?.card ? values.bankName : undefined,
      bankAccount: {
        type: values.bankAccountType ?? undefined,
        accountNumber:
          values.bankAccountType === 'D'
            ? values.accountNumber.replaceAll(' ', '')
            : undefined,
        iban:
          values.bankAccountType === 'F'
            ? values.iban.replaceAll(' ', '').toUpperCase()
            : undefined,
        bankSwift:
          values.bankAccountType === 'F' ? values.swift.toUpperCase() : undefined,
        accountHolder: values.holder ?? '',
      },
    }
  }

  //console.log(params.otherData, 'otherData')

  type FormikData = {
    insurance: boolean | null
    insuranceCompany: string
    card: boolean | null
    bankName: string
    bankAccountType: string | null
    iban: string
    accountNumber: string
    swift: string
    holder: string
  }
  // Transformuje data ze struktury modelu (Resp) do inicialnich struktur formiku
  const transformOtherDataToFormikValue = (
    otherData?: OtherDataType | null | undefined
  ): FormikData => {
    // INICIALIZACE FORMULARE
    return {
      insurance: otherData?.otherInsurance ?? null,
      insuranceCompany: otherData?.otherInsurer ?? '',
      card: otherData?.bankCard ?? null,
      bankName: otherData?.cardIssuer ?? '',
      bankAccountType: otherData?.bankAccount?.type ?? 'D',
      iban: otherData?.bankAccount?.iban ?? '',
      accountNumber: otherData?.bankAccount?.accountNumber ?? '',
      swift: otherData?.bankAccount?.bankSwift ?? '',
      holder: otherData?.bankAccount?.accountHolder ?? '',
    }
  }

  const formikInitialValues = transformOtherDataToFormikValue(params.otherData)

  const BankAccountSchema = () =>
    yup.object().shape({
      insurance: yup.boolean().nullable().required(t('optionRequired')),
      insuranceCompany: yup
        .mixed()
        .nullable()
        .when('insurance', {
          is: (insurance: boolean) => insurance,
          then: yup
            .string()
            .required(t('fifthPg.insuranceCompany.required'))
            .min(2, t('lengthText')),
        }),
      card: yup.boolean().nullable().required(t('optionRequired')),
      bankName: yup
        .mixed()
        .nullable()
        .when('card', {
          is: (card: boolean) => card,
          then: yup
            .string()
            .required(t('fifthPg.bankName.required'))
            .min(2, t('lengthText')),
        }),
      accountNumber: yup
        .mixed()
        .nullable()
        .when('bankAccountType', {
          is: (bankAccountType: string) => bankAccountType === 'D',
          then: yupCheckPlAccountNumber({
            formatErrMsg: t('fifthPg.accountNumber.err2'),
            lengthErrMsg: t('fifthPg.accountNumber.err1')
          }).required(t('fifthPg.accountNumber.required')),
          // yup.string()
          //   .required(t('fifthPg.accountNumber.required'))
          //   .test('len', t('fifthPg.accountNumber.err1'), (val: any) => {
          //     const valueWithoutEmptyMask = val?.replaceAll(' ', '')
          //     return valueWithoutEmptyMask?.length === 26
          //   })
          //   .matches(/^[0-9 ]+$/, t('fifthPg.accountNumber.err2')),
        }),
      iban: yup
        .mixed()
        .nullable()
        .when('bankAccountType', {
          is: (bankAccountType: string) => bankAccountType !== 'D',
          then:
            yupCheckIban({
              formatErrMsg: t('fifthPg.iban.err2'),
              lengthErrMsg: t('fifthPg.iban.wrongLength')
            }).required(t('fifthPg.iban.required')),
          // yup
          // .string()
          // .required(t('fifthPg.iban.required'))            
          // .matches(/^([A-Z]{2})[A-Za-z0-9 ]+$/, t('fifthPg.iban.err2'))
          // .min(15, t('fifthPg.iban.tooShort')),
        }),
      swift: yup
        .mixed()
        .nullable()
        .when('bankAccountType', {
          is: (bankAccountType: string) => bankAccountType !== 'D',
          then: yup
            .string()
            .required(t('fifthPg.swift.required'))
            .matches(/[A-Z]{6}[0-9A-Z]{2,5}/, t('message.err')),
        }),
      holder: yup
        .string()
        .required(t('fifthPg.holder.required'))
        .min(2, t('lengthText')),
    })

  const TicketBankAccountSchema = () =>
    yup.object().shape({
      accountNumber: yup
        .mixed()
        .nullable()
        .when('bankAccountType', {
          is: (bankAccountType: string) => bankAccountType === 'D',
          then: yup
            .string()
            .required(t('fifthPg.accountNumber.required'))
            .test('len', t('fifthPg.accountNumber.err1'), (val: any) => {
              const valueWithoutEmptyMask = val?.replaceAll(' ', '')
              return valueWithoutEmptyMask?.length === 26
            })
            .matches(/^[0-9 ]+$/, t('fifthPg.accountNumber.err2')),
        }),
      iban: yup
        .mixed()
        .nullable()
        .when('bankAccountType', {
          is: (bankAccountType: string) => bankAccountType !== 'D',
          then: yup
            .string()
            .required(t('fifthPg.iban.required'))
            .matches(/^([A-Z]{2})[A-Za-z0-9 ]+$/, t('fifthPg.iban.err2'))
            .min(15, t('fifthPg.iban.tooShort')),
        }),
      swift: yup
        .mixed()
        .nullable()
        .when('bankAccountType', {
          is: (bankAccountType: string) => bankAccountType !== 'D',
          then: yup
            .string()
            .required(t('fifthPg.swift.required'))
            .matches(/[A-Z]{6}[0-9A-Z]{2,5}/, t('message.err')),
        }),
      holder: yup
        .string()
        .required(t('fifthPg.holder.required'))
        .min(2, t('lengthText')),
    })

  const formikRef = useRef(null)
  return (
    <Ga4Form formName={'OtherDataForm'} >
      <Formik
        initialValues={formikInitialValues}
        validationSchema={
          state.caseType !== 'TI' ? BankAccountSchema : TicketBankAccountSchema
        }
        onSubmit={handleFormSubmit}
        enableReinitialize
        innerRef={formikRef}
      >
        {({
          initialValues,
          values,
          errors,
          handleChange,
          touched,
          handleBlur,
          setFieldValue
        }) => {
          const isInternational = values.bankAccountType === 'F'
          const isChanged = isEqual(values, initialValues)
          params.setIfEqual(isChanged)
          return (
            <Form className={classes.root}>
              <Grid
                item
                sm={12}
                xl={12}
                xs={12}
                container
                direction="row"
                className={classes.gridMainForm}
              >
                <Grid
                  item
                  sm={9}
                  xl={9}
                  xs={12}
                  container
                  direction="row"
                  className={classes.gridWorkArea}
                >
                  <Grid
                    item
                    sm={12}
                    xs={12}
                    xl={12}
                    container
                    className={classes.gridResize}
                  >
                    {params.caseType !== 'TI' && (
                      <React.Fragment>
                        <Typography className={classes.text} variant="h3">
                          {t([
                            `fifthPg.title.${params.caseType}`,
                            `fifthPg.title.default`,
                          ])}
                        </Typography>
                        <Grid
                          item
                          xs={12}
                          sm={12}
                          xl={12}
                          container
                          className={classes.gridItem}
                          spacing={2}
                        >
                          <Grid item xl={12} xs={12} sm={12}>
                            <BooleanField
                              aria-label={'insurance'}
                              name={'insurance'}
                              value={values.insurance}
                              label={t('fifthPg.insurance.label')}
                              onValueChange={(value) => setFieldValue('insurance', value)}
                              required
                              yesText={t('yes')}
                              noText={t('no')}
                              error={!!(errors.insurance && touched.insurance)}
                              helperText={
                                errors.insurance && touched.insurance ? errors.insurance : null
                              }
                            />
                          </Grid>
                        </Grid>
                        {values?.insurance && (
                          <Grid
                            item
                            xs={12}
                            sm={12}
                            xl={12}
                            container
                            className={classes.gridItem}
                            spacing={2}
                          >
                            <Grid item xl={12} xs={12} sm={12}>
                              <TextField
                                name="insuranceCompany"
                                value={values.insuranceCompany}
                                error={!!(
                                  errors.insuranceCompany &&
                                  touched.insuranceCompany)
                                }
                                label={t('fifthPg.insuranceCompany.label')}
                                placeholder={t('fifthPg.insuranceCompany.hint')}
                                helperText={
                                  errors.insuranceCompany &&
                                    touched.insuranceCompany
                                    ? errors.insuranceCompany
                                    : null
                                }
                                onChange={handleChange}
                                className={classes.fieldSize}
                                InputProps={{ required: false }}
                                inputProps={{ maxLength: 60 }}
                                onBlur={handleBlur}
                                required
                              />
                            </Grid>
                          </Grid>
                        )}
                        <Grid
                          item
                          xs={12}
                          sm={12}
                          xl={12}
                          container
                          className={classes.gridItem}
                          spacing={2}
                        >
                          <Grid item xl={12} xs={12} sm={12}>
                            <BooleanField
                              aria-label={'card'}
                              name={'card'}
                              value={values.card}
                              label={t('fifthPg.card.label')}
                              onValueChange={(value) => setFieldValue('card', value)}
                              required
                              yesText={t('yes')}
                              noText={t('no')}
                              error={!!(errors.card && touched.card)}
                              helperText={
                                errors.card && touched.card ? errors.card : null
                              }
                            />
                          </Grid>
                        </Grid>
                        {values?.card?.toString() === 'true' && (
                          <Grid
                            item
                            xs={12}
                            sm={12}
                            xl={12}
                            container
                            className={classes.gridItem}
                            spacing={2}
                          >
                            <Grid item xl={12} xs={12} sm={12}>
                              <TextField
                                name="bankName"
                                value={values.bankName}
                                error={!!(errors.bankName && touched.bankName)}
                                label={t('fifthPg.bankName.label')}
                                placeholder={t('fifthPg.bankName.hint')}
                                helperText={
                                  errors.bankName && touched.bankName
                                    ? errors.bankName
                                    : null
                                }
                                onChange={handleChange}
                                className={classes.fieldSize}
                                InputProps={{ required: false }}
                                inputProps={{ maxLength: 60 }}
                                onBlur={handleBlur}
                                required
                              />
                            </Grid>
                          </Grid>
                        )}
                      </React.Fragment>
                    )}
                    <Typography className={classes.text} variant="h3">
                      {t('fifthPg.title2')}
                    </Typography>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid item xl={12} xs={12} sm={12}>
                        <RadioButtonsField
                          aria-label={'bankAccountType'}
                          name={'bankAccountType'}
                          label={t('fifthPg.bankAccountType.label')}
                          required
                          value={values.bankAccountType}
                          onChange={handleChange}
                          lookup={objectToLookup(t('fifthPg.bankAccountType.code', { returnObjects: true }))}
                          error={Boolean(errors.bankAccountType && touched.bankAccountType)}
                          helperText={errors.bankAccountType && touched.bankAccountType
                            ? errors.bankAccountType
                            : null}
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      {!isInternational && (
                        <Grid item xl={6} xs={6} sm={6}>
                          <InputMask
                            name="accountNumber"
                            mask="99 9999 9999 9999 9999 9999 9999"
                            //@ts-ignore
                            maskChar={null}
                            value={values.accountNumber}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          >
                            {(inputProps: any) => (
                              <TextField
                                {...inputProps}
                                error={
                                  errors.accountNumber && touched.accountNumber
                                }
                                label={t('fifthPg.accountNumber.label')}
                                placeholder={t('fifthPg.accountNumber.hint')}
                                onChange={handleChange}
                                helperText={
                                  errors.accountNumber && touched.accountNumber
                                    ? errors.accountNumber
                                    : null
                                }
                                className={classes.fieldSize}
                                inputProps={{ maxLength: 32 }}
                                InputLabelProps={{ required: true }}
                              />
                            )}
                          </InputMask>
                        </Grid>
                      )}
                      {isInternational && (
                        <Grid item xl={6} xs={6} sm={6}>
                          <InputMask
                            name="iban"
                            mask="**** **** **** **** **** **** **** **** **"
                            //@ts-ignore
                            maskChar={null}
                            value={values?.iban?.toUpperCase()}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          >
                            {(inputProps: any) => (
                              <TextField
                                {...inputProps}
                                regex={ibanRegex}
                                error={errors.iban && touched.iban}
                                label={t('fifthPg.iban.label')}
                                placeholder={t('fifthPg.iban.hint')}
                                onChange={handleChange}
                                helperText={
                                  errors.iban && touched.iban ? errors.iban : null
                                }
                                className={classes.fieldSize}
                                inputProps={{ maxLength: 42 }}
                                InputLabelProps={{ required: true }}
                              />
                            )}
                          </InputMask>
                        </Grid>
                      )}
                      {isInternational && (
                        <Grid item xl={6} xs={6} sm={6}>
                          <TextField
                            regex={swiftRegex}
                            name="swift"
                            value={values?.swift?.toUpperCase()}
                            error={!!(errors.swift && touched.swift)}
                            label={t('fifthPg.swift.label')}
                            placeholder={t('fifthPg.swift.hint')}
                            helperText={
                              errors.swift && touched.swift ? errors.swift : null
                            }
                            onChange={handleChange}
                            className={classes.fieldSize}
                            InputProps={{ required: false }}
                            inputProps={{ maxLength: 11 }}
                            onBlur={handleBlur}
                            required
                          />
                        </Grid>
                      )}
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid item xl={12} xs={12} sm={12}>
                        <TextField
                          name="holder"
                          value={values.holder}
                          error={!!(errors.holder && touched.holder)}
                          label={t('fifthPg.holder.label')}
                          placeholder={t('fifthPg.holder.hint')}
                          helperText={
                            errors.holder && touched.holder ? errors.holder : null
                          }
                          onChange={handleChange}
                          className={classes.fieldSize}
                          InputProps={{ required: false }}
                          inputProps={{ maxLength: 60 }}
                          onBlur={handleBlur}
                          required
                        />
                      </Grid>
                    </Grid>
                    <ErrorSubmitBack
                      formik={formikRef.current}
                      pending={params.isContinueInProgress}
                      backLinkText={t('fifthPg.backBtn')}
                      onBackLinkClick={handleBackButtonClicked}
                    >
                      {t('continueBtn')}
                    </ErrorSubmitBack>
                  </Grid>
                </Grid>
              </Grid>
            </Form>
          )
        }}
      </Formik>
    </Ga4Form >
  )
}

export default OtherDataForm
